import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { authActions } from '../store/actions';
import { config } from '../utils';
import { loadGoogleIdentityServices } from '../utils/googleIdentityServices';

const useStyles = makeStyles({
  button: {
    minHeight: 40,
    width: '100%',
    '& > div': {
      margin: '0 auto',
    },
  },
  disabled: {
    opacity: 0.5,
    pointerEvents: 'none',
  },
});

const GoogleSignInButton = ({ disabled = false, onCredential }) => {
  const buttonRef = useRef(null);
  const dispatch = useDispatch();
  const classes = useStyles();
  const { i18n } = useTranslation();
  const language = (i18n.resolvedLanguage || i18n.language || 'en').split(
    '-'
  )[0];

  useEffect(() => {
    let isMounted = true;

    if (!config.google.oauthClientId) {
      dispatch(authActions.googleAuthUnavailable());
      return () => {
        isMounted = false;
      };
    }

    loadGoogleIdentityServices()
      .then((googleIdentityServices) => {
        if (!isMounted || !buttonRef.current) {
          return;
        }

        googleIdentityServices.initialize({
          client_id: config.google.oauthClientId,
          callback: ({ credential }) => {
            if (credential) {
              if (onCredential) {
                onCredential(credential);
              } else {
                dispatch(authActions.googleAuth(credential));
              }
            }
          },
          ux_mode: 'popup',
        });

        buttonRef.current.replaceChildren();
        googleIdentityServices.renderButton(buttonRef.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: 'continue_with',
          shape: 'rectangular',
          logo_alignment: 'left',
          width: Math.floor(buttonRef.current.getBoundingClientRect().width),
          locale: language,
        });
      })
      .catch(() => {
        if (isMounted) {
          dispatch(authActions.googleAuthUnavailable());
        }
      });

    return () => {
      isMounted = false;
    };
  }, [dispatch, language, onCredential]);

  return (
    <div
      ref={buttonRef}
      className={`${classes.button} ${disabled ? classes.disabled : ''}`}
      aria-disabled={disabled}
      data-cy="googleSignInButton"
    />
  );
};

GoogleSignInButton.propTypes = {
  disabled: PropTypes.bool,
  onCredential: PropTypes.func,
};

export default GoogleSignInButton;
